import { useEffect, useState } from 'react';
import { api, TownPrompt, TownPromptSpace } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Icon } from '../icons/Icon';

/**
 * PromptManagement — 週替わりお題管理（Admin／決定3・決定19）
 *
 * トキワ荘「お題スケッチ」／文壇サロン「今週の一筆」のお題を Admin が手動で運用する。
 * 掲出期間内のお題が無いとき、各街区の画面は「次のお題を準備中」を正直に表示する
 * （偽のお題を出さない＝事実性①）。ここでの登録漏れはそのフェイルセーフで安全に受け止められる。
 *
 * UI型は SiteAssetManagement.tsx を踏襲（loading/error/empty の明示・破壊的操作は二段確認・YES/NO禁止）。
 */

const SPACES: { key: TownPromptSpace; label: string; hint: string; chip: string }[] = [
    { key: 'salon', label: '文壇サロン「今週の一筆」', hint: '短い題を出し、書き手が一篇を綴る', chip: 'bg-role-script-soft text-role-script-text border-role-script' },
    { key: 'tokiwa', label: 'トキワ荘「お題スケッチ」', hint: '週替わりモチーフを描き切る', chip: 'bg-role-illustration-soft text-role-illustration-text border-role-illustration' },
];

/** Unix秒 ↔ <input type="date"> の相互変換（ローカル日付基準） */
const toDateInput = (unixSec: number): string => {
    const d = new Date(unixSec * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fromDateInput = (value: string, endOfDay = false): number => {
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return NaN;
    return Math.floor(new Date(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0).getTime() / 1000);
};
const fmt = (unixSec: number) => new Date(unixSec * 1000).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' });

export const PromptManagement = () => {
    const [space, setSpace] = useState<TownPromptSpace>('salon');
    const [prompts, setPrompts] = useState<TownPrompt[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirmId, setConfirmId] = useState<string | null>(null);

    // 作成フォーム（既定は今日から7日間）
    const today = new Date();
    const weekLater = new Date(Date.now() + 7 * 86400 * 1000);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [from, setFrom] = useState(toDateInput(Math.floor(today.getTime() / 1000)));
    const [to, setTo] = useState(toDateInput(Math.floor(weekLater.getTime() / 1000)));

    const load = async (s: TownPromptSpace) => {
        setLoading(true);
        setPrompts(await api.adminListPrompts(s));
        setLoading(false);
    };
    useEffect(() => { load(space); }, [space]);

    const nowSec = Math.floor(Date.now() / 1000);
    const current = prompts.find(p => p.active_from <= nowSec && p.active_to > nowSec) ?? null;

    const create = async () => {
        const activeFrom = fromDateInput(from);
        const activeTo = fromDateInput(to, true);
        if (!title.trim()) { toast({ title: 'お題を入れてください', variant: 'destructive' }); return; }
        if (!Number.isFinite(activeFrom) || !Number.isFinite(activeTo) || activeTo <= activeFrom) {
            toast({ title: '掲出期間が正しくありません', description: '終了日は開始日より後にしてください', variant: 'destructive' });
            return;
        }
        setBusy(true);
        const r = await api.adminCreatePrompt({ space, title: title.trim(), description: description.trim() || undefined, active_from: activeFrom, active_to: activeTo });
        setBusy(false);
        if (!r.success) { toast({ title: 'お題を出せませんでした', description: r.error, variant: 'destructive' }); return; }
        toast({ title: 'お題を出しました', description: '掲出期間に入ると街区に表示されます。' });
        setTitle(''); setDescription('');
        await load(space);
    };

    const remove = async (id: string) => {
        setBusy(true);
        const r = await api.adminDeletePrompt(space, id);
        setBusy(false);
        setConfirmId(null);
        if (!r.success) { toast({ title: '取り下げできませんでした', description: r.error, variant: 'destructive' }); return; }
        toast({ title: 'お題を取り下げました' });
        await load(space);
    };

    return (
        <div className="space-y-6">
            {/* 街区の切替 */}
            <div className="flex flex-wrap gap-2">
                {SPACES.map(s => (
                    <button
                        key={s.key}
                        onClick={() => setSpace(s.key)}
                        aria-pressed={space === s.key}
                        className={`min-h-[44px] px-4 py-2 rounded-pop-sm border-2 font-bold text-sm transition-transform ${space === s.key ? `${s.chip} border-ink` : 'bg-surface text-ink-2 border-ink/20 hover:-translate-y-0.5'}`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {/* 現在の掲出状況（実データのみ。無いことも正直に出す） */}
            <div className="bg-surface border-2 border-ink rounded-pop-md p-4">
                <div className="text-xs font-bold text-ink-2 mb-1">いま掲出中のお題</div>
                {loading ? (
                    <div className="h-5 w-40 bg-cream-2 rounded motion-safe:animate-pulse" aria-label="読み込み中" />
                ) : current ? (
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-ink">{current.title}</span>
                        <span className="text-[11px] text-ink-3 tabular-nums">{fmt(current.active_from)} 〜 {fmt(current.active_to)}</span>
                    </div>
                ) : (
                    <p className="text-sm text-ink-2">
                        掲出中のお題はありません。街区には「次のお題を準備中」と表示されます（偽のお題は出ません）。
                    </p>
                )}
            </div>

            {/* 作成 */}
            <div className="bg-surface border-2 border-ink rounded-pop-md p-4 space-y-3">
                <div className="font-display font-bold text-ink">新しいお題を出す</div>
                <div>
                    <label className="block text-xs text-ink-2 mb-1" htmlFor="prompt-title">お題</label>
                    <input id="prompt-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={100}
                        className="pop-field w-full" placeholder={space === 'salon' ? '例: 雨上がりの匂い' : '例: 窓辺の静物'} />
                </div>
                <div>
                    <label className="block text-xs text-ink-2 mb-1" htmlFor="prompt-desc">補足・ヒント（任意）</label>
                    <textarea id="prompt-desc" value={description} onChange={e => setDescription(e.target.value)} rows={2}
                        className="pop-field w-full resize-none" placeholder="書き出し・描き方のヒント" />
                </div>
                <div className="flex gap-3 flex-wrap">
                    <div>
                        <label className="block text-xs text-ink-2 mb-1" htmlFor="prompt-from">掲出開始</label>
                        <input id="prompt-from" type="date" value={from} onChange={e => setFrom(e.target.value)} className="pop-field min-h-[44px]" />
                    </div>
                    <div>
                        <label className="block text-xs text-ink-2 mb-1" htmlFor="prompt-to">掲出終了</label>
                        <input id="prompt-to" type="date" value={to} onChange={e => setTo(e.target.value)} className="pop-field min-h-[44px]" />
                    </div>
                </div>
                <button onClick={create} disabled={busy} className="btn btn-primary min-h-[44px]">
                    <Icon name="plus" size={16} /> {busy ? '出しています…' : 'このお題を出す'}
                </button>
            </div>

            {/* 一覧 */}
            <div className="space-y-2">
                <div className="text-xs font-bold text-ink-2">登録済みのお題</div>
                {loading ? (
                    <div className="space-y-2">
                        {[0, 1].map(i => <div key={i} className="h-14 bg-cream-2 rounded-pop-sm motion-safe:animate-pulse" />)}
                    </div>
                ) : prompts.length === 0 ? (
                    <p className="text-sm text-ink-3">まだお題はありません。最初の一題を出しましょう。</p>
                ) : (
                    <ul className="space-y-2 list-none p-0 m-0">
                        {prompts.map(p => {
                            const isCurrent = p.active_from <= nowSec && p.active_to > nowSec;
                            return (
                                <li key={p.id} className="bg-surface border-2 border-ink/20 rounded-pop-sm p-3 flex items-center gap-3 flex-wrap">
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-ink text-sm">{p.title}</span>
                                            {isCurrent && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sun text-ink border border-ink">掲出中</span>}
                                        </div>
                                        {p.description && <p className="text-xs text-ink-2 mt-0.5 line-clamp-1">{p.description}</p>}
                                        <span className="text-[11px] text-ink-3 tabular-nums">{fmt(p.active_from)} 〜 {fmt(p.active_to)}</span>
                                    </div>
                                    {confirmId === p.id ? (
                                        <div className="flex gap-2">
                                            <button onClick={() => setConfirmId(null)} className="btn btn-ghost btn-sm min-h-[44px]">やっぱりやめる</button>
                                            <button onClick={() => remove(p.id)} disabled={busy} className="btn btn-sm min-h-[44px] bg-pop-dark text-white border-2 border-ink rounded-full px-4">取り下げる</button>
                                        </div>
                                    ) : (
                                        <button onClick={() => setConfirmId(p.id)} className="btn btn-ghost btn-sm min-h-[44px]">手放す</button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};
